import { useCallback, useEffect, useState } from "react";
import api, { runJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Facebook, Instagram, Twitter, Linkedin, MapPin, Sparkles, Loader2, Trash2, Send, Calendar } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/brand";

const PLATFORMS = [
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
  { id: "twitter", label: "X / Twitter", icon: Twitter, color: "#0F1419" },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  { id: "gbp", label: "Google Business", icon: MapPin, color: "#34A853" },
];

export default function Social() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [platforms, setPlatforms] = useState(["facebook", "instagram"]);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [scheduledAt, setScheduledAt] = useState("");
  const [gen, setGen] = useState(false);

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);
  const load = useCallback((cid) => { if (cid) api.get(`/clients/${cid}/social`).then((r) => setPosts(r.data)); }, []);
  useEffect(() => { load(clientId); }, [clientId, load]);

  const toggle = (id) => setPlatforms((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const aiGenerate = async () => {
    if (!clientId) return;
    setGen(true);
    try {
      const data = await runJob(api.post(`/clients/${clientId}/social/generate`, { topic, tone, platform: platforms[0] || "general" }));
      setContent(data.caption || "");
      setHashtags(data.hashtags || []);
      toast.success("Draft generated");
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setGen(false); }
  };

  const save = async (status) => {
    if (!content.trim()) return toast.error("Write or generate some content first");
    if (!platforms.length) return toast.error("Select at least one platform");
    await api.post(`/clients/${clientId}/social`, {
      content, hashtags, platforms, status, scheduled_at: status === "scheduled" ? scheduledAt : null,
    });
    toast.success(status === "scheduled" ? "Scheduled" : "Saved to drafts");
    setContent(""); setHashtags([]); setScheduledAt("");
    load(clientId);
  };

  const publish = async (id) => {
    await api.post(`/social/${id}/publish`);
    toast.success("Published (simulated — connect accounts to go live)");
    load(clientId);
  };
  const del = async (id) => { await api.delete(`/social/${id}`); load(clientId); };

  const statusColor = { draft: "bg-slate-100 text-slate-600", scheduled: "bg-blue-50 text-blue-700", published: "bg-emerald-50 text-emerald-700" };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Social Studio</h1>
          <p className="text-slate-500 text-sm mt-1">Compose, AI-generate & schedule posts across platforms</p>
        </div>
        {isStaff && (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-48" data-testid="social-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-slate-200 space-y-4">
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <button key={p.id} onClick={() => toggle(p.id)} data-testid={`platform-${p.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${platforms.includes(p.id) ? "text-white border-transparent" : "text-slate-500 border-slate-200 bg-white"}`}
                style={platforms.includes(p.id) ? { background: p.color } : {}}>
                <p.icon size={14} /> {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">AI topic</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. new client win" className="mt-1.5" data-testid="social-topic" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="mt-1.5" data-testid="social-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{["professional", "friendly", "bold", "informative", "playful"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button variant="outline" onClick={aiGenerate} disabled={gen || !clientId} className="w-full" data-testid="social-generate-btn">
            {gen ? <><Loader2 size={15} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkles size={15} className="mr-1.5" /> AI Generate caption</>}
          </Button>
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Post content</Label>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} className="mt-1.5" placeholder="Write your post or generate with AI…" data-testid="social-content" /></div>
          {hashtags.length > 0 && <div className="flex flex-wrap gap-1.5">{hashtags.map((h, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-sky-50 text-sky-700">{h}</span>)}</div>}
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-slate-400" />
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="flex-1" data-testid="social-schedule" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => save("draft")} variant="outline" className="flex-1" data-testid="social-draft-btn">Save draft</Button>
            <Button onClick={() => save("scheduled")} disabled={!scheduledAt} className="flex-1 text-white" style={{ background: "var(--tenant-primary)" }} data-testid="social-schedule-btn">Schedule</Button>
          </div>
          <p className="text-[11px] text-slate-400">Note: Publishing is simulated until your social accounts are connected.</p>
        </Card>

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-slate-400">Posts ({posts.length})</p>
          {posts.length === 0 && <Card className="p-10 text-center border-slate-200 text-slate-400 text-sm">No posts yet.</Card>}
          {posts.map((p) => (
            <Card key={p.id} className="p-4 border-slate-200" data-testid="social-post">
              <div className="flex items-center gap-2 mb-2">
                {(p.platforms || []).map((pid) => { const M = PLATFORMS.find((x) => x.id === pid); return M ? <M.icon key={pid} size={15} style={{ color: M.color }} /> : null; })}
                <span className={`ml-auto text-[10px] px-2 py-0.5 rounded ${statusColor[p.status] || statusColor.draft}`}>{p.status}{p.simulated ? " (sim)" : ""}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.content}</p>
              {p.hashtags?.length > 0 && <p className="text-[11px] text-sky-600 mt-1">{p.hashtags.join(" ")}</p>}
              <div className="flex items-center gap-2 mt-3">
                {p.scheduled_at && <span className="text-[11px] text-slate-400">⏰ {fmtDate(p.scheduled_at)}</span>}
                {p.status !== "published" && <Button size="sm" onClick={() => publish(p.id)} className="h-7 text-xs text-white ml-auto" style={{ background: "var(--tenant-primary)" }} data-testid="social-publish-btn"><Send size={12} className="mr-1" /> Publish</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="h-7 px-2" data-testid="social-del-btn"><Trash2 size={13} className="text-red-400" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
